/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  static findVariant (variants, selectedOptions) {
    return variants.find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )
  }

  static isOptionValueAvailable (variants, selectedOptions, optionIndex, optionValue) {
    const candidateOptions = [...selectedOptions]
    candidateOptions[optionIndex] = optionValue
    return Boolean(ProductForm.findVariant(variants, candidateOptions)?.available)
  }

  updateVariantImageOptionAvailability (selectedOptions) {
    this.form.querySelectorAll('[data-variant-image-option]').forEach(item => {
      const optionIndex = Number(item.dataset.optionPosition) - 1
      const optionValue = item.dataset.optionValue
      const available = ProductForm.isOptionValueAvailable(
        this.variants,
        selectedOptions,
        optionIndex,
        optionValue
      )
      const label = item.querySelector('[data-variant-image-label]') ||
        (item.matches?.('[data-variant-image-label]') ? item : null)
      const overlay = item.querySelector('[data-variant-sold-out-overlay]')

      item.classList.toggle('is-sold-out', !available)
      overlay?.toggleAttribute('hidden', available)
      label?.setAttribute(
        'aria-label',
        available ? optionValue : `${optionValue}（${this.soldOutLabel}）`
      )
    })
  }

  getSelectedOptions () {
    return Array.from(this.productOptions).flatMap(elem => {
      if (elem.type === 'radio') return elem.checked ? [elem.value] : []
      if (elem.type === 'select-one') return [elem.value]
      return []
    })
  }

  constructor () {
    super()

    this.form = this.querySelector('form')
    this.variants = JSON.parse(this.form.dataset.variants || '[]')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.productOptionsContainer = this.form.querySelector('.product-options')
    this.soldOutLabel = this.productOptionsContainer?.dataset.soldOutLabel || window.theme.locales.product.sold_out
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    this.updateVariantImageOptionAvailability(this.getSelectedOptions())
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = this.getSelectedOptions()
    const selectedVariant = ProductForm.findVariant(this.variants, selectedOptions)

    this.updateVariantImageOptionAvailability(selectedOptions)

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)

    await document.querySelector('cart-container').add(formData)

    this.atcBtn.classList.remove('loading')
    this.atcBtn.disabled = false
    this.atcBtn.setAttribute('aria-busy', 'false')
  }

  onClickBuyBtn () {
    this.buyBtn.classList.add('loading')
    const variantId = this.form.querySelector('[name="id"]').value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
    window.location.href = `/cart/${variantId}:${qty}`
  }
}
customElements.define('product-form', ProductForm)

class KsInventoryBar extends HTMLElement {
  constructor () {
    super()
    this.refreshToken = 0
    this.onVariantChange = this.onVariantChange.bind(this)
  }

  connectedCallback () {
    window.addEventListener('kt.product.variant.change', this.onVariantChange)
  }

  disconnectedCallback () {
    window.removeEventListener('kt.product.variant.change', this.onVariantChange)
  }

  onVariantChange (event) {
    const variant = event.detail
    if (!variant) return
    if (variant.product_id && String(variant.product_id) !== String(this.dataset.productId)) return

    this.dataset.currentVariantId = String(variant.id)
    this.applyAvailability(Boolean(variant.available))
    this.refresh(variant.id)
  }

  applyAvailability (available) {
    const text = this.querySelector('.ks-inventory-bar-text')
    const progress = this.querySelector('.bs-progress-bar')
    const prefix = available ? 'inStock' : 'soldOut'
    const color = available ? this.dataset.colorHasStock : this.dataset.colorSoldOut

    text.textContent = this.dataset[`${prefix}Text`]
    progress.style.width = this.dataset[`${prefix}Width`]
    progress.dataset.width = this.dataset[`${prefix}Width`]
    this.style.setProperty('--color', color)
  }

  async refresh (variantId) {
    const token = ++this.refreshToken
    this.setAttribute('aria-busy', 'true')

    try {
      const url = new URL(window.location.href)
      url.searchParams.set('variant', variantId)
      const response = await fetch(url, { credentials: 'same-origin' })

      if (!response.ok) {
        throw new Error(`Inventory refresh failed: ${response.status}`)
      }

      const html = await response.text()
      const nextDocument = new DOMParser().parseFromString(html, 'text/html')
      const replacement = nextDocument.getElementById(this.id)
      const isLatest = token === this.refreshToken &&
        String(variantId) === String(this.dataset.currentVariantId)

      if (replacement && isLatest) {
        this.replaceWith(replacement)
      } else if (isLatest) {
        this.removeAttribute('aria-busy')
      }
    } catch (error) {
      if (token === this.refreshToken) {
        this.removeAttribute('aria-busy')
      }
    }
  }
}

if (!customElements.get?.('ks-inventory-bar')) {
  customElements.define('ks-inventory-bar', KsInventoryBar)
}

class ProductMediaGallery extends HTMLElement {
  static getVisibleMediaIds (allMediaIds, variantMediaIds, selectedMediaId) {
    const allIds = allMediaIds.map(String)
    const variantIds = new Set(variantMediaIds.filter(Boolean).map(String))
    const selectedId = selectedMediaId ? String(selectedMediaId) : ''

    if (!selectedId || !variantIds.has(selectedId) || !allIds.includes(selectedId)) {
      return allIds
    }

    return [
      selectedId,
      ...allIds.filter(mediaId => mediaId !== selectedId && !variantIds.has(mediaId))
    ]
  }

  constructor () {
    super()

    this.mainElement = this.querySelector('.swiper-main')
    this.thumbsElement = this.querySelector('.swiper-thumbs')
    this.mainWrapper = this.mainElement.querySelector('.swiper-wrapper')
    this.thumbsWrapper = this.thumbsElement.querySelector('.swiper-wrapper')
    this.mainSlideMarkup = this.captureSlideMarkup(this.mainWrapper)
    this.thumbSlideMarkup = this.captureSlideMarkup(this.thumbsWrapper)
    this.allMediaIds = Array.from(this.mainSlideMarkup.keys())
    this.variantMediaIds = (this.dataset.variantMediaIds || '')
      .split(',')
      .map(mediaId => mediaId.trim())
      .filter(Boolean)

    const selectedMediaId = this.dataset.selectedMediaId
    const hasSelectedVariantMedia = this.variantMediaIds.includes(String(selectedMediaId)) &&
      this.allMediaIds.includes(String(selectedMediaId))

    this.renderMedia(selectedMediaId)
    this.initializeSwipers(hasSelectedVariantMedia ? 0 : Number(this.dataset.initialSlide))

    this.setStickyPosition()
    this.listenVariantChange()
  }

  captureSlideMarkup (wrapper) {
    return new Map(
      Array.from(wrapper.children)
        .filter(slide => slide.dataset.mediaId)
        .map(slide => [String(slide.dataset.mediaId), slide.outerHTML])
    )
  }

  renderMedia (selectedMediaId) {
    const visibleMediaIds = ProductMediaGallery.getVisibleMediaIds(
      this.allMediaIds,
      this.variantMediaIds,
      selectedMediaId
    )

    this.mainWrapper.innerHTML = visibleMediaIds
      .map(mediaId => this.mainSlideMarkup.get(mediaId))
      .filter(Boolean)
      .join('')
    this.thumbsWrapper.innerHTML = visibleMediaIds
      .map(mediaId => this.thumbSlideMarkup.get(mediaId))
      .filter(Boolean)
      .join('')
    this.thumbsElement.toggleAttribute('hidden', visibleMediaIds.length < 2)
    this.currentMediaIds = visibleMediaIds

    return visibleMediaIds
  }

  initializeSwipers (initialSlide) {
    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.thumbsElement, this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.mainElement, this.mainOptions())

    this.main.slideTo(initialSlide)
  }

  destroySwipers () {
    if (this.main && !this.main.destroyed) {
      this.main.destroy(true, true)
    }
    if (this.thumbs && !this.thumbs.destroyed) {
      this.thumbs.destroy(true, true)
    }
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }
    return options
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail
      const selectedMediaId = selectedVariant.featured_media?.id

      if (
        selectedVariant.product_id &&
        String(selectedVariant.product_id) !== String(this.dataset.productId)
      ) {
        return
      }

      if (selectedMediaId && !this.allMediaIds.includes(String(selectedMediaId))) {
        return
      }

      const visibleMediaIds = ProductMediaGallery.getVisibleMediaIds(
        this.allMediaIds,
        this.variantMediaIds,
        selectedMediaId
      )
      const mediaIsUnchanged = visibleMediaIds.length === this.currentMediaIds.length &&
        visibleMediaIds.every((mediaId, index) => mediaId === this.currentMediaIds[index])

      if (mediaIsUnchanged) {
        if (selectedMediaId) {
          this.main.slideTo(0)
        }
        return
      }

      const hasSelectedVariantMedia = this.variantMediaIds.includes(String(selectedMediaId)) &&
        this.allMediaIds.includes(String(selectedMediaId))

      this.destroySwipers()
      this.renderMedia(selectedMediaId)
      this.initializeSwipers(hasSelectedVariantMedia ? 0 : Number(this.dataset.initialSlide))
    }, false)
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
